import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';

const currency = (value) => `$${(Number(value) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const COST_FIELDS = [
  ['labor', 'Labor'],
  ['materials', 'Materials'],
  ['fuel', 'Fuel'],
  ['equipment', 'Equipment'],
  ['subcontractor', 'Subcontractor'],
  ['permit', 'Permit'],
  ['disposal', 'Disposal'],
  ['misc', 'Misc'],
];

const blankCosts = {
  labor: '',
  materials: '',
  fuel: '',
  equipment: '',
  subcontractor: '',
  permit: '',
  disposal: '',
  misc: '',
};

export const PerformanceMetricsModule = ({ onBack, currentUser }) => {
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [jobDetail, setJobDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [costs, setCosts] = useState(blankCosts);
  const [invoiceId, setInvoiceId] = useState('');
  const [revenueInput, setRevenueInput] = useState('');
  const [revenueMode, setRevenueMode] = useState('manual');
  const [notes, setNotes] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const canEdit = ['admin', 'manager'].includes(currentUser?.rawRole || '');

  const loadJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const res = await axios.get(`${API}/job-performance-metrics/jobs`);
      const items = res.data || [];
      setJobs(items);
      if (!selectedJobId && items.length > 0) {
        setSelectedJobId(items[0].id);
      }
      if (selectedJobId && !items.find((j) => j.id === selectedJobId)) {
        setSelectedJobId(items[0]?.id || '');
      }
    } catch (err) {
      console.error('Failed to load jobs for performance metrics', err);
    } finally {
      setLoadingJobs(false);
    }
  }, [selectedJobId]);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const res = await axios.get(`${API}/job-performance-metrics/summary`);
      setSummary(res.data || null);
    } catch (err) {
      console.error('Failed to load performance summary', err);
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const loadJobDetail = useCallback(async (jobId) => {
    if (!jobId) {
      setJobDetail(null);
      setCosts(blankCosts);
      setInvoiceId('');
      setRevenueInput('');
      setRevenueMode('manual');
      setNotes('');
      return;
    }

    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/job-performance-metrics/jobs/${jobId}`);
      const detail = res.data;
      setJobDetail(detail);
      setCosts({
        labor: detail?.costs?.labor ?? '',
        materials: detail?.costs?.materials ?? '',
        fuel: detail?.costs?.fuel ?? '',
        equipment: detail?.costs?.equipment ?? '',
        subcontractor: detail?.costs?.subcontractor ?? '',
        permit: detail?.costs?.permit ?? '',
        disposal: detail?.costs?.disposal ?? '',
        misc: detail?.costs?.misc ?? '',
      });
      setInvoiceId(detail?.invoice_id || detail?.invoice_candidates?.[0]?.id || '');
      if (detail?.has_saved_metrics) {
        setRevenueInput(String(Number(detail?.revenue_amount || 0)));
        setRevenueMode(detail?.worth_source === 'invoice' ? 'invoice' : 'manual');
      } else {
        setRevenueInput('');
        setRevenueMode('manual');
      }
      setNotes(detail?.notes || '');
    } catch (err) {
      console.error('Failed to load job performance detail', err);
      setJobDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
    loadSummary();
  }, [loadJobs, loadSummary]);

  useEffect(() => {
    loadJobDetail(selectedJobId);
  }, [selectedJobId, loadJobDetail]);

  useEffect(() => {
    setSaveMessage('');
  }, [selectedJobId]);

  const selectedJobMeta = useMemo(() => jobs.find((j) => j.id === selectedJobId) || null, [jobs, selectedJobId]);
  const costTotal = useMemo(() => COST_FIELDS.reduce((sum, [key]) => sum + (Number(costs[key]) || 0), 0), [costs]);
  const suggestedInvoiceTotal = Number(jobDetail?.invoice_total_suggested || 0);
  const suggestedInvoiceLabel = jobDetail?.invoice_label_suggested || '';
  const revenue = Number(revenueInput || 0);
  const grossProfit = revenue - costTotal;
  const marginPercent = revenue > 0 ? (grossProfit / revenue) * 100 : 0;

  const handleSave = async () => {
    if (!selectedJobId || !canEdit) return;
    setSaveLoading(true);
    setSaveMessage('');
    try {
      await axios.put(`${API}/job-performance-metrics/jobs/${selectedJobId}`, {
        invoice_id: invoiceId,
        revenue_amount: Number(revenueInput) || 0,
        use_invoice_total: revenueMode === 'invoice',
        costs: {
          labor: Number(costs.labor) || 0,
          materials: Number(costs.materials) || 0,
          fuel: Number(costs.fuel) || 0,
          equipment: Number(costs.equipment) || 0,
          subcontractor: Number(costs.subcontractor) || 0,
          permit: Number(costs.permit) || 0,
          disposal: Number(costs.disposal) || 0,
          misc: Number(costs.misc) || 0,
        },
        notes,
      });
      setSaveMessage('Saved performance metrics successfully.');
      await loadJobDetail(selectedJobId);
      await Promise.all([loadSummary(), loadJobs()]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to save performance metrics.';
      setSaveMessage(detail);
    } finally {
      setSaveLoading(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-800" data-testid="performance-metrics-no-access-page">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <button onClick={onBack} className="text-sm text-gray-600 hover:text-gray-800 mb-4" data-testid="performance-metrics-back-btn">← Back to Management</button>
          <h1 className="text-2xl font-bold" data-testid="performance-metrics-no-access-title">Performance Metrics</h1>
          <p className="text-sm text-gray-600 mt-2" data-testid="performance-metrics-no-access-message">Only Admin and Manager users can access this tool.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-gray-800" data-testid="performance-metrics-page">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <button onClick={onBack} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium" data-testid="performance-metrics-back-btn">← Back to Management</button>
            <h1 className="text-3xl font-bold mt-2" data-testid="performance-metrics-title">Performance Metrics</h1>
            <p className="text-sm text-gray-600 mt-1" data-testid="performance-metrics-subtitle">Overall revenue vs expenses at top, then simple per-job metric input.</p>
          </div>
          <button
            onClick={async () => {
              await Promise.all([loadJobs(), loadSummary()]);
              await loadJobDetail(selectedJobId);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100"
            data-testid="performance-metrics-refresh-btn"
          >
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="performance-overall-metrics-cards">
          <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="performance-overall-revenue-card">
            <p className="text-xs uppercase text-gray-500">Overall Revenue</p>
            <p className="text-2xl font-semibold text-emerald-700" data-testid="performance-overall-revenue-value">
              {loadingSummary ? '...' : currency(summary?.total_revenue)}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="performance-overall-expenses-card">
            <p className="text-xs uppercase text-gray-500">Overall Expenses</p>
            <p className="text-2xl font-semibold" data-testid="performance-overall-expenses-value">
              {loadingSummary ? '...' : currency(summary?.total_cost)}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="performance-overall-profit-card">
            <p className="text-xs uppercase text-gray-500">Overall Profit</p>
            <p className={`text-2xl font-semibold ${Number(summary?.gross_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="performance-overall-profit-value">
              {loadingSummary ? '...' : currency(summary?.gross_profit)}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-testid="performance-overall-margin-value">
              Margin: {Number(summary?.margin_percent || 0).toFixed(2)}%
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4" data-testid="performance-job-input-panel">
          <div>
            <h2 className="text-lg font-semibold" data-testid="performance-job-input-title">Per Job Metrics</h2>
            <p className="text-sm text-gray-600" data-testid="performance-job-input-subtitle">Select a job, enter revenue/costs, then save.</p>
          </div>

          <div>
            <label className="text-xs uppercase text-gray-500" data-testid="performance-job-selector-label">Job</label>
            <select
              value={selectedJobId}
              onChange={(e) => setSelectedJobId(e.target.value)}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              data-testid="performance-job-select"
              disabled={loadingJobs}
            >
              <option value="">Select a job...</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.customer_name || 'Unnamed'} • {job.job_location || 'No location'}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2" data-testid="performance-job-selected-meta">
              {selectedJobMeta
                ? `Selected: ${selectedJobMeta.customer_name || 'N/A'} • ${selectedJobMeta.job_location || 'N/A'} • Status: ${selectedJobMeta.job_status || 'N/A'}`
                : 'Select a job to continue.'}
            </p>
          </div>

          {loadingDetail ? (
            <p className="text-sm text-gray-500" data-testid="performance-job-detail-loading">Loading job details...</p>
          ) : !jobDetail ? (
            <p className="text-sm text-gray-500" data-testid="performance-job-detail-empty">Select a job to input metrics.</p>
          ) : (
            <>
              <div>
                <label className="text-xs uppercase text-gray-500" data-testid="performance-revenue-input-label">Revenue</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={revenueInput}
                  onChange={(e) => {
                    setRevenueInput(e.target.value);
                    setRevenueMode('manual');
                  }}
                  className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Enter revenue for this job"
                  data-testid="performance-revenue-input"
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setRevenueInput(String(suggestedInvoiceTotal));
                      setRevenueMode('invoice');
                      if (!invoiceId && jobDetail?.invoice_candidates?.[0]?.id) {
                        setInvoiceId(jobDetail.invoice_candidates[0].id);
                      }
                    }}
                    disabled={!suggestedInvoiceTotal}
                    className="px-3 py-1.5 rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                    data-testid="performance-use-invoice-total-btn"
                  >
                    Use Invoice Total: {currency(suggestedInvoiceTotal)}
                  </button>
                  <span
                    className={`text-xs px-2 py-1 rounded-full border ${revenueMode === 'invoice' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}
                    data-testid="performance-revenue-mode-badge"
                  >
                    {revenueMode === 'invoice' ? 'Using invoice total' : 'Manual revenue'}
                  </span>
                </div>
                {suggestedInvoiceLabel ? (
                  <p className="text-xs text-gray-500 mt-2" data-testid="performance-invoice-reference-label">Invoice reference: {suggestedInvoiceLabel}</p>
                ) : null}
              </div>

              <div className="space-y-2" data-testid="performance-cost-list">
                <p className="text-xs uppercase text-gray-500" data-testid="performance-cost-inputs-label">Costs</p>
                {COST_FIELDS.map(([key, label]) => (
                  <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`performance-cost-row-${key}`}>
                    <label className="text-sm text-gray-700 sm:w-40" data-testid={`performance-cost-label-${key}`}>{label}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={costs[key]}
                      onChange={(e) => setCosts((prev) => ({ ...prev, [key]: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      data-testid={`performance-cost-input-${key}`}
                    />
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="performance-live-calculations">
                <div className="border border-gray-200 rounded-lg p-3" data-testid="performance-live-revenue-card">
                  <p className="text-xs text-gray-500 uppercase">Revenue</p>
                  <p className="text-lg font-semibold text-emerald-700" data-testid="performance-live-revenue">{currency(revenue)}</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3" data-testid="performance-live-cost-card">
                  <p className="text-xs text-gray-500 uppercase">Expenses</p>
                  <p className="text-lg font-semibold" data-testid="performance-live-total-cost">{currency(costTotal)}</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3" data-testid="performance-live-profit-card">
                  <p className="text-xs text-gray-500 uppercase">Profit</p>
                  <p className={`text-lg font-semibold ${grossProfit >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="performance-live-gross-profit">
                    {currency(grossProfit)} ({marginPercent.toFixed(2)}%)
                  </p>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase text-gray-500" data-testid="performance-notes-label">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="performance-notes-input"
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleSave}
                  disabled={saveLoading}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                  data-testid="performance-save-btn"
                >
                  {saveLoading ? 'Saving...' : 'Save Metrics'}
                </button>
                <p className="text-sm text-gray-600" data-testid="performance-save-message">{saveMessage}</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetricsModule;
